import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Alert, Chip, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Stack, Stepper, Step, StepLabel, useTheme, IconButton, Divider
} from '@mui/material';
import {
  CheckCircle as PassIcon, Cancel as FailIcon, Search as InspectIcon,
  Build as FixIcon, Verified as VerifyIcon, Close as CloseIcon, Warning as WarnIcon,
  TrendingDown as DownIcon, TrendingUp as UpIcon, Remove as UnchangedIcon
} from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';

interface FifoMovement {
  movementId: number;
  movementDate: string;
  sourceType?: string;
  purchaseInvoiceDetailId?: number;
  quantity: number;
  currentRemaining: number;
  currentConsumed: number;
  expectedRemaining: number;
  expectedConsumed: number;
  delta: number;
  unitCost: number;
  changeIndicator?: string;
}

interface FifoLocation {
  locationKey: string;
  itemId: number;
  itemCode?: string;
  itemName?: string;
  itemNameAr?: string;
  unitId: number;
  unitName?: string;
  unitNameAr?: string;
  warehouseId?: number;
  branchId?: number;
  locationDisplay?: string;
  locationDisplayAr?: string;
  totalConsumed: number;
  totalRemaining: number;
  allCostsUniform: boolean;
  safeToAutoFix: boolean;
  unsafeReason?: string;
  unsafeReasonAr?: string;
  movements: FifoMovement[];
}

interface DetectResult {
  checkedAt: string;
  costingMethod: string;
  totalLocations: number;
  totalMovementsAffected: number;
  locations: FifoLocation[];
}

interface InspectCheck { checkName: string; checkNameAr: string; passed: boolean; detail?: string; }
interface InspectLocation { locationKey: string; itemName?: string; itemNameAr?: string; checks: InspectCheck[]; allPassed: boolean; }
interface InspectResult { allPassed: boolean; totalLocations: number; passedLocations: number; locations: InspectLocation[]; warnings: string[]; }

interface ApplyChange { movementId: number; beforeRemaining: number; afterRemaining: number; delta: number; }
interface ApplyLocationResult { locationKey: string; itemId: number; itemCode?: string; itemName?: string; itemNameAr?: string; locationDisplay?: string; movementsChanged: number; totalRedistributed: number; changes: ApplyChange[]; }
interface ApplyResult { isSuccess: boolean; message?: string; messageAr?: string; locationsFixed: number; movementsChanged: number; totalUnitsRedistributed: number; results: ApplyLocationResult[]; operationLog: string[]; finalFifoIssueCount: number; completedAt: string; }

interface Props { open: boolean; onClose: () => void; isRTL: boolean; onFixComplete?: () => void; }

const FifoFixDialog: React.FC<Props> = ({ open, onClose, isRTL, onFixComplete }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [stage, setStage] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [detect, setDetect] = useState<DetectResult | null>(null);
  const [inspect, setInspect] = useState<InspectResult | null>(null);
  const [apply, setApply] = useState<ApplyResult | null>(null);

  useEffect(() => {
    if (open) { setStage(0); setDetect(null); setInspect(null); setApply(null); runDetect(); }
  }, [open]);

  const runDetect = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<DetectResult>('/DataIntegrity/fifo-order/detect');
      setDetect(data);
    } catch (err: any) { toast.error(err.response?.data?.message || 'Detect failed'); }
    finally { setLoading(false); }
  };

  const runInspect = async () => {
    if (!detect) return;
    setLoading(true);
    try {
      const safeLocs = detect.locations.filter(l => l.safeToAutoFix).map(l => l.locationKey);
      if (safeLocs.length === 0) {
        toast.warning(isRTL ? 'لا يوجد مواقع آمنة للإصلاح التلقائي' : 'No locations safe for auto-fix');
        return;
      }
      const { data } = await axiosInstance.post<InspectResult>('/DataIntegrity/fifo-order/inspect', { locationKeys: safeLocs });
      setInspect(data);
      setStage(1);
    } catch (err: any) { toast.error(err.response?.data?.message || 'Inspect failed'); }
    finally { setLoading(false); }
  };

  const runApply = async () => {
    if (!inspect) return;
    setLoading(true);
    try {
      const passedKeys = inspect.locations.filter(l => l.allPassed).map(l => l.locationKey);
      const { data } = await axiosInstance.post<ApplyResult>('/DataIntegrity/fifo-order/apply', { locationKeys: passedKeys });
      setApply(data);
      setStage(2);
      if (data.isSuccess) {
        toast.success(isRTL ? data.messageAr || 'تم' : data.message || 'OK');
        if (onFixComplete) onFixComplete();
      } else {
        toast.error(isRTL ? data.messageAr || 'فشل' : data.message || 'Failed');
      }
    } catch (err: any) { toast.error(err.response?.data?.message || 'Apply failed'); }
    finally { setLoading(false); }
  };

  const renderChangeIcon = (indicator?: string) => {
    if (indicator === 'increase') return <UpIcon sx={{ color: '#4caf50', fontSize: 16 }} />;
    if (indicator === 'decrease') return <DownIcon sx={{ color: '#f44336', fontSize: 16 }} />;
    return <UnchangedIcon sx={{ color: '#9e9e9e', fontSize: 16 }} />;
  };

  const renderDetect = () => {
    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (!detect) return null;
    if (detect.costingMethod !== 'FIFO') {
      return <Alert severity="info">{isRTL ? `سياسة التسعير الحالية: ${detect.costingMethod} (ليست FIFO) — لا حاجة لإصلاح ترتيب FIFO` : `Costing method: ${detect.costingMethod} (not FIFO) — no FIFO fix needed`}</Alert>;
    }
    if (detect.totalLocations === 0) {
      return <Alert severity="success" icon={<PassIcon />}>{isRTL ? '🎉 ترتيب FIFO سليم لكل الأصناف' : '🎉 FIFO order correct for all items'}</Alert>;
    }
    const safeCount = detect.locations.filter(l => l.safeToAutoFix).length;
    const unsafeCount = detect.locations.length - safeCount;
    return (
      <Stack spacing={2}>
        <Alert severity="warning" icon={<WarnIcon />}>
          {isRTL
            ? `وجدنا ${detect.totalLocations} موقع بخرق ترتيب FIFO (${detect.totalMovementsAffected} حركة متأثرة). ${safeCount} آمن للإصلاح التلقائي، ${unsafeCount} يحتاج Inventory Rebalance.`
            : `Found ${detect.totalLocations} locations with FIFO violations (${detect.totalMovementsAffected} movements affected). ${safeCount} safe for auto-fix, ${unsafeCount} need Inventory Rebalance.`}
        </Alert>
        {detect.locations.map(loc => (
          <Paper key={loc.locationKey} elevation={2} sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${loc.safeToAutoFix ? '#4caf50' : '#ff9800'}` }}>
            <Stack spacing={1.5}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip label={loc.itemCode || `Item ${loc.itemId}`} size="small" color="primary" />
                <Typography variant="h6">{isRTL ? loc.itemNameAr : loc.itemName}</Typography>
                <Chip label={isRTL ? loc.locationDisplayAr : loc.locationDisplay} size="small" variant="outlined" />
                <Chip
                  label={loc.safeToAutoFix ? (isRTL ? 'آمن للإصلاح' : 'Safe to fix') : (isRTL ? 'يحتاج مراجعة' : 'Manual review')}
                  size="small"
                  color={loc.safeToAutoFix ? 'success' : 'warning'}
                />
              </Box>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Typography variant="body2"><strong>{isRTL ? 'إجمالي المُستهلك:' : 'Total Consumed:'}</strong> {loc.totalConsumed.toFixed(4)}</Typography>
                <Typography variant="body2"><strong>{isRTL ? 'إجمالي المتبقي:' : 'Total Remaining:'}</strong> {loc.totalRemaining.toFixed(4)}</Typography>
                <Typography variant="body2"><strong>{isRTL ? 'تكلفة موحدة:' : 'Uniform Cost:'}</strong> {loc.allCostsUniform ? '✅' : '❌'}</Typography>
              </Box>
              {!loc.safeToAutoFix && loc.unsafeReason && (
                <Alert severity="warning" sx={{ fontSize: '0.85rem' }}>{isRTL ? loc.unsafeReasonAr : loc.unsafeReason}</Alert>
              )}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'التاريخ' : 'Date'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'المصدر' : 'Source'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">{isRTL ? 'إجمالي' : 'Qty'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#d32f2f' }} align="right">{isRTL ? 'متبقي حالياً' : 'Current Rem'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32' }} align="right">{isRTL ? 'متبقي متوقع' : 'Expected Rem'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">{isRTL ? 'تغيير' : 'Δ'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loc.movements.map(m => (
                      <TableRow key={m.movementId} sx={m.changeIndicator !== 'unchanged' ? { bgcolor: isDark ? 'rgba(255,193,7,0.06)' : '#fffde7' } : undefined}>
                        <TableCell>#{m.movementId}</TableCell>
                        <TableCell>{new Date(m.movementDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</TableCell>
                        <TableCell>{m.sourceType || (m.purchaseInvoiceDetailId ? 'PI' : '—')}</TableCell>
                        <TableCell align="right">{m.quantity.toFixed(4)}</TableCell>
                        <TableCell align="right" sx={{ color: '#d32f2f' }}>{m.currentRemaining.toFixed(4)}</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: m.changeIndicator !== 'unchanged' ? 'bold' : 'normal' }}>{m.expectedRemaining.toFixed(4)}</TableCell>
                        <TableCell align="center">{renderChangeIcon(m.changeIndicator)} {m.delta !== 0 && <span style={{ fontSize: '0.75rem' }}>{m.delta > 0 ? '+' : ''}{m.delta.toFixed(4)}</span>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  };

  const renderInspect = () => {
    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (!inspect) return null;
    return (
      <Stack spacing={2}>
        <Alert severity={inspect.allPassed ? 'success' : 'error'} icon={inspect.allPassed ? <PassIcon /> : <FailIcon />}>
          {isRTL ? `${inspect.passedLocations}/${inspect.totalLocations} موقع اجتاز الفحوصات` : `${inspect.passedLocations}/${inspect.totalLocations} locations passed`}
        </Alert>
        {inspect.locations.map(l => (
          <Paper key={l.locationKey} elevation={1} sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${l.allPassed ? '#4caf50' : '#f44336'}` }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>{l.allPassed ? '✅' : '❌'} {isRTL ? l.itemNameAr : l.itemName}</Typography>
            <Stack spacing={0.5}>
              {l.checks.map((c, i) => (
                <Box key={i} display="flex" alignItems="center" gap={1}>
                  {c.passed ? <PassIcon sx={{ color: '#4caf50', fontSize: 18 }} /> : <FailIcon sx={{ color: '#f44336', fontSize: 18 }} />}
                  <Typography variant="body2">{isRTL ? c.checkNameAr : c.checkName}</Typography>
                  {c.detail && <Typography variant="caption" color="text.secondary">— {c.detail}</Typography>}
                </Box>
              ))}
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  };

  const renderApply = () => {
    if (loading) return <Box display="flex" flexDirection="column" alignItems="center" p={4} gap={2}><CircularProgress /><Typography>{isRTL ? 'جارٍ التطبيق...' : 'Applying...'}</Typography></Box>;
    if (!apply) return null;
    return (
      <Stack spacing={2}>
        <Alert severity={apply.isSuccess ? 'success' : 'error'} icon={apply.isSuccess ? <VerifyIcon /> : <FailIcon />}>
          <Typography variant="h6">{apply.isSuccess ? (isRTL ? '✅ تم الإصلاح بنجاح' : '✅ Fix Applied') : (isRTL ? '❌ فشل' : '❌ Failed')}</Typography>
          <Typography variant="body2">{isRTL ? apply.messageAr : apply.message}</Typography>
        </Alert>

        {apply.isSuccess && apply.results.length > 0 && (
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', borderRadius: '8px 8px 0 0' }}>
              <Typography variant="subtitle1" fontWeight="bold">{isRTL ? 'تفاصيل الإصلاح لكل موقع' : 'Per-Location Fix Details'}</Typography>
            </Box>
            {apply.results.map((r, i) => (
              <Box key={i} sx={{ p: 2, borderBottom: i < apply.results.length - 1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` : 'none' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>{r.itemCode} — {isRTL ? r.itemNameAr : r.itemName}</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{isRTL ? 'حركة' : 'Movement'}</TableCell>
                        <TableCell align="right" sx={{ color: '#d32f2f' }}>{isRTL ? 'قبل' : 'Before'}</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32' }}>{isRTL ? 'بعد' : 'After'}</TableCell>
                        <TableCell align="right">{isRTL ? 'فرق' : 'Δ'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {r.changes.map(c => (
                        <TableRow key={c.movementId}>
                          <TableCell>#{c.movementId}</TableCell>
                          <TableCell align="right" sx={{ color: '#d32f2f' }}>{c.beforeRemaining.toFixed(4)}</TableCell>
                          <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>{c.afterRemaining.toFixed(4)}</TableCell>
                          <TableCell align="right">{c.delta > 0 ? '+' : ''}{c.delta.toFixed(4)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
            <Box sx={{ p: 1.5, bgcolor: isDark ? 'rgba(76,175,80,0.1)' : '#f1f8e9', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="body2"><strong>{isRTL ? 'المواقع المُصلَحة:' : 'Locations Fixed:'}</strong> {apply.locationsFixed}</Typography>
              <Typography variant="body2"><strong>{isRTL ? 'حركات مُعدَّلة:' : 'Movements Changed:'}</strong> {apply.movementsChanged}</Typography>
              <Typography variant="body2"><strong>{isRTL ? 'وحدات أُعيد توزيعها:' : 'Units Redistributed:'}</strong> {apply.totalUnitsRedistributed.toFixed(4)}</Typography>
              <Typography variant="body2"><strong>{isRTL ? 'خروقات FIFO متبقية:' : 'Remaining FIFO violations:'}</strong> <Chip size="small" label={apply.finalFifoIssueCount} color={apply.finalFifoIssueCount === 0 ? 'success' : 'warning'} /></Typography>
            </Box>
          </Paper>
        )}

        {apply.operationLog.length > 0 && (
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? 'rgba(0,0,0,0.3)' : '#f5f5f5' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>{isRTL ? '📋 سجل العمليات' : '📋 Operation Log'}</Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.8rem', maxHeight: 200, overflow: 'auto' }}>
              {apply.operationLog.map((line, i) => <Box key={i}>{line}</Box>)}
            </Box>
          </Paper>
        )}
      </Stack>
    );
  };

  const steps = isRTL ? ['الكشف', 'فحوصات السلامة', 'التطبيق', 'النتيجة'] : ['Detect', 'Inspect', 'Apply', 'Result'];
  const currentStep = apply ? 3 : inspect ? (stage === 1 ? 1 : 2) : 0;
  const safeCount = detect?.locations.filter(l => l.safeToAutoFix).length ?? 0;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="lg" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)', color: 'white' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <FixIcon /> {isRTL ? '🔧 إصلاح ترتيب FIFO' : '🔧 Fix FIFO Order'}
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} disabled={loading}><CloseIcon /></IconButton>
      </DialogTitle>
      <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa' }}>
        <Stepper activeStep={currentStep} alternativeLabel>
          {steps.map((label, i) => <Step key={i}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
      </Box>
      <DialogContent sx={{ minHeight: 350 }}>
        {stage === 0 && renderDetect()}
        {stage === 1 && renderInspect()}
        {stage === 2 && renderApply()}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {stage === 0 && detect && detect.totalLocations > 0 && safeCount > 0 && (
          <>
            <Button onClick={onClose} disabled={loading}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
            <Button variant="contained" startIcon={<InspectIcon />} onClick={runInspect} disabled={loading}>{isRTL ? `فحص للتأكد (${safeCount})` : `Inspect (${safeCount})`}</Button>
          </>
        )}
        {stage === 1 && inspect && (
          <>
            <Button onClick={() => setStage(0)} disabled={loading}>{isRTL ? 'رجوع' : 'Back'}</Button>
            <Button variant="contained" color="success" startIcon={<FixIcon />} onClick={runApply}
              disabled={loading || !inspect.allPassed}
              sx={inspect.allPassed ? { background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' } : undefined}>
              {isRTL ? '✅ تأكيد الإصلاح' : '✅ Confirm Fix'}
            </Button>
          </>
        )}
        {stage === 2 && <Button variant="contained" onClick={onClose}>{isRTL ? 'إغلاق' : 'Close'}</Button>}
        {(stage === 0 && (!detect || detect.totalLocations === 0 || safeCount === 0)) && (
          <Button onClick={onClose}>{isRTL ? 'إغلاق' : 'Close'}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FifoFixDialog;
